import React, { useEffect, useMemo, useState } from "react";
import { hqApi } from "@/hq/hqApi";
import { Card, EmptyState, Skeleton } from "@/hq/HQPrimitives";
import { toast } from "sonner";
import PitchDetailModal from "@/hq/campaigns/PitchDetailModal";
import BulkFollowUpGeneratorModal from "@/hq/campaigns/BulkFollowUpGeneratorModal";
import { INDUSTRY_CATALOGUE } from "@/hq/industries";
import { Send, CheckSquare, Square } from "lucide-react";

const PITCH_TYPES = [
  { v: "",         l: "All types" }, { v: "website",  l: "Website" },
  { v: "optimise", l: "Optimise" },  { v: "crm",      l: "CRM" },
  { v: "full",     l: "Full Pitch" },
];
const ENGAGEMENTS = [
  { v: "",           l: "All engagement" }, { v: "sent",       l: "Sent" },
  { v: "opened",     l: "Opened" },         { v: "clicked",    l: "Clicked" },
  { v: "interested", l: "Interested" },     { v: "not_opened", l: "Not opened" },
];
const STATE_TONE = {
  INTERESTED: { fg: "#0F7B3E", bg: "#EBFBF2", bd: "#B7EBCF" },
  CLICKED:    { fg: "#8A2E00", bg: "#FFF7EE", bd: "#FCE1C7" },
  OPENED:     { fg: "#8A6D00", bg: "#FFF9E6", bd: "#F5E4B0" },
  SENT:       { fg: "#4A4A4A", bg: "#F6F5F2", bd: "#EAEAEA" },
  FAILED:     { fg: "#C1350B", bg: "#FFECEC", bd: "#F5C4C4" },
  SKIPPED:    { fg: "#8A8A8A", bg: "#F6F5F2", bd: "#EAEAEA" },
  PENDING:    { fg: "#8A8A8A", bg: "#F6F5F2", bd: "#EAEAEA" },
};
function StateChip({ state, testId }) {
  const tone = STATE_TONE[state] || STATE_TONE.PENDING;
  return (<span data-testid={testId}
    className="inline-flex items-center px-2 py-1 rounded-md text-[10px] font-black tracking-[0.06em] uppercase whitespace-nowrap"
    style={{ color: tone.fg, background: tone.bg, border: `1px solid ${tone.bd}` }}>{state}</span>);
}
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString(undefined,
    { year: "2-digit", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
};
const isEligibleRow = (r) =>
  r.state !== "failed" && !!r.email && r.pitch_type !== undefined;

export default function HQPitches() {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [pitchType, setPitchType] = useState("");
  const [industry, setIndustry] = useState("");
  const [territory, setTerritory] = useState("");
  const [engagement, setEngagement] = useState("");
  const [selected, setSelected] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulk, setShowBulk] = useState(false);

  const load = () => {
    setItems(null);
    const params = { page, page_size: 25 };
    if (q) params.q = q;
    if (pitchType) params.pitch_type = pitchType;
    if (industry)  params.industry = industry;
    if (territory) params.territory = territory;
    if (engagement) params.engagement = engagement;
    hqApi.get("/control/pitches", { params })
      .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => { setItems([]); toast.error("Failed to load pitches"); });
  };
  useEffect(load, [page, pitchType, industry, territory, engagement]);
  useEffect(() => { const h = setTimeout(load, 300); return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const pages = useMemo(() => Math.max(1, Math.ceil(total / 25)), [total]);

  // Reset selection whenever filters change so we never accidentally
  // carry a hidden selection across filter changes.
  useEffect(() => { setSelectedIds(new Set()); }, [page, q, pitchType, industry, territory, engagement]);

  const eligibleOnPage = useMemo(
    () => (items || []).filter(isEligibleRow), [items]);
  const allPageSelected = eligibleOnPage.length > 0
    && eligibleOnPage.every((r) => selectedIds.has(r.id));

  const toggleRow = (id, e) => {
    if (e) e.stopPropagation();
    setSelectedIds((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };
  const toggleAllOnPage = () => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (allPageSelected) eligibleOnPage.forEach((r) => n.delete(r.id));
      else eligibleOnPage.forEach((r) => n.add(r.id));
      return n;
    });
  };

  return (
    <div className="space-y-4" data-testid="hq-pitches-tab">
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
          <input data-testid="hq-pitches-search"
            className="md:col-span-2 px-3 py-2 rounded-full border border-[#EAEAEA] text-[13px]"
            placeholder="Search business, contact or email…"
            value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }}/>
          <select data-testid="hq-pitches-filter-pitch-type" value={pitchType}
            onChange={(e) => { setPage(1); setPitchType(e.target.value); }}
            className="px-3 py-2 rounded-full border border-[#EAEAEA] text-[13px] bg-white">
            {PITCH_TYPES.map((t) => <option key={t.v || "any"} value={t.v}>{t.l}</option>)}
          </select>
          <select data-testid="hq-pitches-filter-industry" value={industry}
            onChange={(e) => { setPage(1); setIndustry(e.target.value); }}
            className="px-3 py-2 rounded-full border border-[#EAEAEA] text-[13px] bg-white">
            <option value="">All industries</option>
            {INDUSTRY_CATALOGUE.map((i) => <option key={i.slug} value={i.slug}>{i.label}</option>)}
          </select>
          <input data-testid="hq-pitches-filter-territory"
            className="px-3 py-2 rounded-full border border-[#EAEAEA] text-[13px]"
            placeholder="Territory" value={territory}
            onChange={(e) => { setPage(1); setTerritory(e.target.value); }}/>
          <select data-testid="hq-pitches-filter-engagement" value={engagement}
            onChange={(e) => { setPage(1); setEngagement(e.target.value); }}
            className="px-3 py-2 rounded-full border border-[#EAEAEA] text-[13px] bg-white">
            {ENGAGEMENTS.map((e) => <option key={e.v || "any"} value={e.v}>{e.l}</option>)}
          </select>
        </div>
        <div className="mt-2 flex items-center justify-between">
          <div className="text-[11px] tracking-[0.16em] uppercase font-bold text-[#8A8A8A]">
            <span data-testid="hq-pitches-total">{total} pitches</span>
          </div>
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-3">
              <span data-testid="hq-pitches-selected-count"
                    className="text-[11px] tracking-[0.16em] uppercase font-bold text-[#FF6A00]">
                {selectedIds.size} selected
              </span>
              <button onClick={() => setSelectedIds(new Set())}
                      data-testid="hq-pitches-clear-selection"
                      className="text-[10px] tracking-[0.16em] uppercase font-black text-[#8A8A8A] hover:text-[#0A0A0A]">Clear</button>
              <button onClick={() => setShowBulk(true)}
                      data-testid="hq-pitches-bulk-followup-btn"
                      className="px-3 py-1.5 rounded-full bg-[#FF6A00] text-white text-[10px] tracking-[0.16em] uppercase font-black hover:bg-[#e35c00] inline-flex items-center gap-1">
                <Send size={11}/> Send bulk follow-up
              </button>
            </div>
          )}
        </div>
      </Card>

      <Card className="overflow-x-auto">
        {items === null ? (<div className="p-6"><Skeleton h={200}/></div>)
        : items.length === 0 ? (<EmptyState title="No pitches match these filters" testId="hq-pitches-empty" hint="Try clearing filters or sending a new pitch from a client profile."/>)
        : (
          <table className="w-full text-[13px]" data-testid="hq-pitches-table">
            <thead className="text-[10px] tracking-[0.12em] uppercase font-black text-[#8A8A8A] bg-[#F6F5F2]">
              <tr>
                <th className="text-left px-2 py-2 w-8">
                  <button onClick={toggleAllOnPage} data-testid="hq-pitches-select-all-page"
                          aria-label="Select page" className="inline-flex items-center">
                    {allPageSelected ? <CheckSquare size={16} className="text-[#FF6A00]"/> : <Square size={16} className="text-[#8A8A8A]"/>}
                  </button>
                </th>
                <th className="text-left px-3 py-2">Business</th>
                <th className="text-left px-3 py-2">Contact</th>
                <th className="text-left px-3 py-2">Industry</th>
                <th className="text-left px-3 py-2">Territory</th>
                <th className="text-left px-3 py-2">Pitch</th>
                <th className="text-left px-3 py-2">Subject</th>
                <th className="text-left px-3 py-2">Sent</th>
                <th className="text-right px-3 py-2">Opens</th>
                <th className="text-right px-3 py-2">Clicks</th>
                <th className="text-left px-3 py-2">State</th>
                <th className="text-right px-3 py-2">Score</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EAEAEA]">
              {items.map((r) => {
                const checked = selectedIds.has(r.id);
                const eligible = isEligibleRow(r);
                return (
                  <tr key={r.id} onClick={() => setSelected(r.id)}
                      data-testid={`hq-pitches-row-${r.id}`}
                      className={`cursor-pointer hover:bg-[#FFF7EE] ${checked ? "bg-[#FFF7EE]" : ""}`}>
                    <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                      {eligible ? (
                        <button onClick={(e) => toggleRow(r.id, e)}
                                data-testid={`hq-pitches-select-${r.id}`}
                                aria-label={checked ? "Deselect" : "Select"} className="inline-flex items-center">
                          {checked ? <CheckSquare size={16} className="text-[#FF6A00]"/> : <Square size={16} className="text-[#8A8A8A]"/>}
                        </button>
                      ) : <span className="text-[#EAEAEA]"><Square size={16}/></span>}
                    </td>
                    <td className="px-3 py-2 font-semibold">{r.business_name || "—"}</td>
                    <td className="px-3 py-2">
                      <div>{r.contact_name || "—"}</div>
                      <div className="text-[11px] text-[#8A8A8A]">{r.email || ""}</div>
                    </td>
                    <td className="px-3 py-2">{r.industry || "—"}</td>
                    <td className="px-3 py-2">{r.territory || "—"}</td>
                    <td className="px-3 py-2 uppercase text-[11px] font-black tracking-[0.14em] text-[#FF6A00]">
                      {r.pitch_type === "full" ? "Full" : (r.pitch_type || "—")}
                    </td>
                    <td className="px-3 py-2 max-w-[220px] truncate" title={r.subject}>{r.subject || "—"}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{fmtDate(r.sent_at)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.open_count || 0}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.click_count || 0}</td>
                    <td className="px-3 py-2"><StateChip state={r.engagement} testId={`hq-pitches-state-${r.id}`}/></td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.orange_score ?? "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      {pages > 1 && (
        <div className="flex items-center justify-between text-[12px]" data-testid="hq-pitches-pagination">
          <div>Page {page} of {pages}</div>
          <div className="flex gap-2">
            <button data-testid="hq-pitches-prev" disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="px-3 py-1.5 rounded-full border border-[#EAEAEA] disabled:opacity-40">Prev</button>
            <button data-testid="hq-pitches-next" disabled={page >= pages}
                    onClick={() => setPage((p) => Math.min(pages, p + 1))}
                    className="px-3 py-1.5 rounded-full border border-[#EAEAEA] disabled:opacity-40">Next</button>
          </div>
        </div>
      )}

      <PitchDetailModal commId={selected} onClose={() => setSelected(null)} onChanged={load}/>
      {showBulk && (
        <BulkFollowUpGeneratorModal
          selectedIds={Array.from(selectedIds)}
          onClose={() => setShowBulk(false)}
          onSent={() => { setSelectedIds(new Set()); load(); }}
        />
      )}
    </div>
  );
}
